import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import './ETPOSDashboard.css';
import apiService from './services/api';
import dateUtils from './utils/dateUtils';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Joyride, STATUS } from 'react-joyride';
import Home from './components/ETPOS/Home';
import Users from './components/ETPOS/Users';
import Contacts from './components/ETPOS/Contacts';
import Products from './components/ETPOS/Products';
import Purchases from './components/ETPOS/Purchases';
import ListPurchases from './components/ETPOS/ListPurchases';
import PurchaseRequisition from './components/ETPOS/PurchaseRequisition';
import PurchaseOrder from './components/ETPOS/PurchaseOrder';
import ListPurchaseReturn from './components/ETPOS/ListPurchaseReturn';
import AddPurchase from './components/ETPOS/AddPurchase';
import Sell from './components/Sell';
import StockTransfers from './components/ETPOS/StockTransfers';
import StockAdjustment from './components/ETPOS/StockAdjustment';
import Expenses from './components/ETPOS/Expenses';
import PaymentAccounts from './components/ETPOS/PaymentAccounts';
import Accounting from './components/ETPOS/Accounting';
import Reports from './components/ETPOS/Reports';
import NotificationTemplates from './components/ETPOS/NotificationTemplates';
import Settings from './components/ETPOS/Settings';
import CRM from './components/ETPOS/CRM';
import WoocommerceConnector from './components/ETPOS/WoocommerceConnector';
import PlaidConnector from './components/ETPOS/PlaidConnector';
import Manufacturing from './components/ETPOS/Manufacturing';
import Repair from './components/ETPOS/Repair';
import Project from './components/ETPOS/Project';
import HRM from './components/ETPOS/HRM';
import Essentials from './components/ETPOS/Essentials';
import Calculator from './components/Calculator';
import TodayProfitModal from './components/Modals/TodayProfitModal';
// SuperAdmin component
import SystemSettings from './components/ETPOS/SystemSettings';
import { usePermissionContext } from './contexts/PermissionContext';
import { useSubscriptionContext } from './contexts/SubscriptionContext';
import { useBusinessSettings } from './contexts/BusinessSettingsContext';
import WhatsAppSupport from './components/Support/WhatsAppSupport';
import SubscriptionGuard from './components/SubscriptionGuard';

// WhatsApp Cloud API Configuration (replace placeholders with real values)
const WHATSAPP_API_VERSION = 'v18.0';
const WHATSAPP_PHONE_NUMBER_ID = 'YOUR_PHONE_NUMBER_ID';
const WHATSAPP_ACCESS_TOKEN = 'YOUR_ACCESS_TOKEN';
const SUPPORT_WHATSAPP_NUMBER = 'YOUR_SUPPORT_NUMBER';

// Isolated clock component — re-renders every second without touching the parent dashboard
const LiveClock = () => {
  const { formatDate, formatTime } = useBusinessSettings();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  return <span>📅 <strong>{formatDate(now)} {formatTime(now)}</strong></span>;
};

const ETPOSDashboard = () => {
  const { hasPermission, hasAnyPermission, isSuperAdmin: isSuperAdminFromContext, isLoading: permissionLoading, enabledModules } = usePermissionContext();
  const { hasModule, isLoading: subscriptionLoading } = useSubscriptionContext();
  const { formatDate, formatTime } = useBusinessSettings();

  const [activeTab, setActiveTab] = useState(() => {
    const cleanPath = window.location.pathname.replace(/\/$/, '');
    const isSalesOrderAdd = cleanPath === '/sell/add-sales-order' || 
      (cleanPath === '/sell/add' && window.location.search.includes('sale_type=sales_order'));
    if (isSalesOrderAdd) return 'sales_order';

    const routeTabMap = {
      '/sell/sales-order': 'sales_order',
      '/sell/sales-orders': 'sales_order',
      '/sales_order': 'sales_order',
      '/sales-order': 'sales_order',
      '/sell/add-sales-order': 'sales_order',
      '/sell/pos-list': 'list_pos',
      '/pos': 'pos',
      '/sell/add': 'add_sale',
      '/sell/add-draft': 'add_draft',
      '/sell/add-quotation': 'add_quotation',
      '/sell/quotation-list': 'list_quotation',
      '/sell/quotations': 'list_quotation',
      '/sell/draft-list': 'list_draft',
      '/sell/drafts': 'list_draft',
      '/sell/sales-returns': 'sales_returns',
      '/sell/shipment': 'shipment',
      '/sell/shipments': 'shipment',
      '/sell/discount': 'discount',
      '/sell/discounts': 'discount',
      '/sell/all': 'all_sales',
      '/sell': 'all_sales',
      '/all_sales': 'all_sales',
    };
    if (routeTabMap[cleanPath]) return routeTabMap[cleanPath];
    const pathParts = window.location.pathname.split('/').filter(p => p);
    return pathParts[0] || 'home';
  });
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Close sidebar by default on mobile devices
    return window.innerWidth > 1024;
  });
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [todoDropdownOpen, setTodoDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showCalculatorPopup, setShowCalculatorPopup] = useState(false);
  const [showTodayProfitModal, setShowTodayProfitModal] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [tourSteps] = useState([
    {
      target: '.menu-toggle',
      content: 'Click here to toggle the sidebar menu.',
      disableBeacon: true,
    },
    {
      target: '.store-title',
      content: 'This is your business name.',
    },
    {
      target: '.pos-btn',
      content: 'Quick access to the Point of Sale interface.',
    },
    {
      target: '.todo-btn',
      content: 'Access utilities like Calendar, Calculator, and Tour.',
    },
    {
      target: '.notification-btn',
      content: 'Check your latest notifications and alerts here.',
    },
    {
      target: '.user-section',
      content: 'Manage your profile, settings, and logout from here.',
    }
  ]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];
    if (finishedStatuses.includes(status)) {
      setRunTour(false);
    }
  };
  const [todayProfitData, setTodayProfitData] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('etpos_dark_mode') === 'true');
  const [notificationsData, setNotificationsData] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await apiService.getNotifications();
        if (response && response.data) {
          setNotificationsData(response.data);
          setUnreadCount(response.data.filter(n => !n.read_at).length);
        }
      } catch (error) {
        console.error("Failed to fetch notifications", error);
      }
    };
    fetchNotifications();
    // Refresh notifications every 60 seconds
    const intervalId = setInterval(fetchNotifications, 60000);
    return () => clearInterval(intervalId);
  }, []);
  const [currentUser, setCurrentUser] = useState(null);
  const isInitialLoad = useRef(true);
  const [expandedMenus, setExpandedMenus] = useState({ purchases: true, reports: true, users: false });
  const userSectionRef = useRef(null);
  const todoDropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const calendarRef = useRef(null);
  const calculatorRef = useRef(null);
  const todayProfitRef = useRef(null);
  const contentWrapperRef = useRef(null);

  const [hoveredItem, setHoveredItem] = useState(null);
  const [clickedMenuId, setClickedMenuId] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);

  // Close active submenus on mobile click outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.etpos-sidebar')) {
        setClickedMenuId(null);
      }
    };
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  // Apply dark mode class to root element
  useEffect(() => {
    document.documentElement.classList.toggle('dark-mode', darkMode);
    localStorage.setItem('etpos_dark_mode', darkMode);
  }, [darkMode]);

  const handleLogout = () => {
    // Clear all business, user, products, permissions and authentication cache
    apiService.clearCachedBusinessSettings();

    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();

    // Trigger settings context reload
    window.dispatchEvent(new Event('etpos-settings-update'));

    // Navigate to login page with replace to prevent back navigation
    navigate('/login', { replace: true });

    // Clear browser history to prevent back button access
    window.history.pushState(null, '', '/login');
  };

  const isImpersonating = !!localStorage.getItem('etpos_superadmin_backup_token');
  const handleSwitchBackToSuperadmin = () => {
    const backupToken = localStorage.getItem('etpos_superadmin_backup_token');
    if (backupToken) {
      apiService.clearCachedBusinessSettings();

      const backupUserStr = localStorage.getItem('etpos_superadmin_backup_user');
      if (backupUserStr) {
        try {
          const backupUser = JSON.parse(backupUserStr);
          localStorage.setItem('etpos_user', backupUserStr);
          const bizName = backupUser.business?.name || backupUser.business_name;
          const bizLogo = backupUser.business?.logo || backupUser.business_logo;
          if (bizName) localStorage.setItem('etpos_business_name', bizName);
          if (bizLogo) localStorage.setItem('etpos_business_logo', bizLogo);
        } catch (e) {}
      }

      const expiryTime = new Date();
      expiryTime.setHours(expiryTime.getHours() + 24);

      localStorage.setItem('etpos_token', backupToken);
      localStorage.setItem('etpos_token_expiry', expiryTime.toISOString());
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.removeItem('etpos_superadmin_backup_token');
      localStorage.removeItem('etpos_superadmin_backup_user');
      toast.success('Restored Superadmin session');
      window.location.href = '/system-settings';
    }
  };

  const toggleUserDropdown = () => {
    setUserDropdownOpen(!userDropdownOpen);
  };

  const toggleNotificationDropdown = () => {
    setNotificationDropdownOpen(!notificationDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userSectionRef.current && !userSectionRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
      if (todoDropdownRef.current && !todoDropdownRef.current.contains(event.target)) {
        setTodoDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationDropdownOpen(false);
      }
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
      if (calculatorRef.current && !calculatorRef.current.contains(event.target)) {
        setShowCalculatorPopup(false);
      }
      if (todayProfitRef.current && !todayProfitRef.current.contains(event.target)) {
        setShowTodayProfitModal(false);
      }
    };

    if (userDropdownOpen || todoDropdownOpen || showCalendar || showTodayProfitModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userDropdownOpen, todoDropdownOpen, showCalendar, showCalculatorPopup, showTodayProfitModal]);

  const fetchUserData = async () => {
    // Only fetch if user is authenticated
    if (!apiService.isTokenValid()) {
      /* console.log('User not authenticated, skipping user data fetch'); */
      return;
    }

    try {
      // Get user details
      const userDetails = await apiService.getUserDetails();

      // Get business locations to get business info
      try {
        const locationsData = await apiService.getBusinessLocations();

        // Extract business info from locations
        let businessInfo = null;
        if (locationsData?.data && locationsData.data.length > 0) {
          businessInfo = {
            name: locationsData.data[0]?.name || 'ET-POS Store',
            location_id: locationsData.data[0]?.id
          };
        } else if (Array.isArray(locationsData) && locationsData.length > 0) {
          businessInfo = {
            name: locationsData[0]?.name || 'ET-POS Store',
            location_id: locationsData[0]?.id
          };
        }

        // Fetch user roles separately
        let userRoles = [];
        try {
          const rolesData = await apiService.getUserRoles();
          userRoles = rolesData.data || [];
      /* console.log('🔐 USER ROLES - Fetched user roles:', userRoles); */
        } catch (rolesError) {
      /* console.error('Error fetching user roles:', rolesError); */
        }

        // Combine user, business info, and roles (preserving true business name and logo)
        const combinedData = {
          ...userDetails.data,  // Extract user data from the data property
          business: {
            ...businessInfo,
            ...(userDetails.data?.business || {})
          },
          roles: userRoles  // Add roles to user data
        };
      /* console.log('🔐 USER ROLES - Combined user data with roles:', combinedData); */
        setCurrentUser(combinedData);
        localStorage.setItem('etpos_user', JSON.stringify(combinedData));
        if (combinedData.business?.name) localStorage.setItem('etpos_business_name', combinedData.business.name);
        else localStorage.removeItem('etpos_business_name');

        if (combinedData.business?.logo) localStorage.setItem('etpos_business_logo', combinedData.business.logo);
        else localStorage.removeItem('etpos_business_logo');

      } catch (locationError) {
      /* console.error('Error fetching business locations:', locationError); */
        // Still set user data even if business fetch fails
        setCurrentUser(userDetails.data || userDetails);
      }

    } catch (error) {
      /* console.error('Error fetching current user:', error); */
      // Don't show error to user if it's just authentication issue
      if (!error.message.includes('authentication')) {
        // Fallback to default if API fails
        const fallbackUser = {
          first_name: 'User',
          last_name: '',
          username: 'user'
        };
        setCurrentUser(fallbackUser);
      }
    } finally {
      isInitialLoad.current = false;
    }
  };

  // Fetch current user details and business info on component mount
  useEffect(() => {
    fetchUserData();
  }, []);

  // Fetch today's profit data
  const fetchTodayProfitData = async () => {
    try {
      // Get today's date range
      const today = dateUtils.getChicagoToday();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

      // Format dates for API
      const startDate = startOfDay.toISOString().split('T')[0];
      const endDate = endOfDay.toISOString().split('T')[0];

      // Fetch sales data for today
      const salesResponse = await apiService.getSales({
        start_date: startDate,
        end_date: endDate
      });

      // Calculate profit from sales data
      let totalRevenue = 0;
      let totalCost = 0;
      let totalProfit = 0;
      const profitItems = [];

      const salesArray = Array.isArray(salesResponse) ? salesResponse : (salesResponse?.data || []);

      if (salesArray.length > 0) {
        salesArray.forEach(sale => {
          const revenue = parseFloat(sale.final_total) || 0;
          const cost = parseFloat(sale.total_cost) || 0;
          const profit = revenue - cost;

          totalRevenue += revenue;
          totalCost += cost;
          totalProfit += profit;

          profitItems.push({
            id: sale.id,
            invoiceNo: sale.invoice_no,
            customer: sale.contact?.name || sale.customer?.name || 'Walk-in Customer',
            revenue: revenue,
            cost: cost,
            profit: profit,
            time: formatTime(new Date(sale.transaction_date))
          });
        });
      }

      setTodayProfitData({
        date: formatDate(today),
        totalRevenue,
        totalCost,
        totalProfit,
        items: profitItems,
        totalTransactions: profitItems.length
      });
    } catch (error) {
      /* console.error('Error fetching today profit data:', error); */
      // Set default data on error
      setTodayProfitData({
        date: formatDate(new Date()),
        totalRevenue: 0,
        totalCost: 0,
        totalProfit: 0,
        items: [],
        totalTransactions: 0,
        error: 'Failed to load profit data'
      });
    }
  };

  const menuCategories = [
    {
      title: 'Main',
      items: [
        {
          id: 'system-settings',
          label: 'Superadmin',
          icon: '👑',
          permission: 'superadmin.access',
          subItems: [
            { id: 'superadmin-dashboard', label: 'Superadmin', icon: 'fa fa-bank', permission: 'superadmin.access' },
            { id: 'superadmin-business', label: 'All Business', icon: 'fa fa-bank', permission: 'superadmin.access' },
            { id: 'superadmin-subscription', label: 'Subscription', icon: 'fa fa-refresh', permission: 'superadmin.access' },
            { id: 'superadmin-packages', label: 'Subscription Packages', icon: 'fa fa-credit-card', permission: 'superadmin.access' },
            { id: 'superadmin-coupons', label: 'All Coupons', icon: 'fa fa-ticket', permission: 'superadmin.access' },
            { id: 'superadmin-settings', label: 'Super Admin Settings', icon: 'fa fa-cogs', permission: 'superadmin.access' },
            { id: 'superadmin-communicator', label: 'Communicator', icon: 'fa fa-envelope', permission: 'superadmin.access' },
          ]
        },
        { id: 'home', label: 'Home', icon: '🏠', permission: null },
        {
          id: 'users',
          label: 'User Management',
          icon: '👥',
          permission: ['user.view', 'user.create', 'roles.view'],
          subItems: [
            { id: 'users', label: 'Users', icon: 'fa fa-user', permission: 'user.view' },
            { id: 'roles', label: 'Roles', icon: 'fa fa-key', permission: 'roles.view' },
            { id: 'agents', label: 'Sales Commission Agents', icon: 'fa fa-briefcase', permission: 'user.create' },
          ]
        },
        {
          id: 'contacts',
          label: 'Contacts',
          icon: '📇',
          permission: ['supplier.view', 'customer.view', 'supplier.view_own', 'customer.view_own'],
          subItems: [
            { id: 'suppliers', label: 'Suppliers', icon: 'fa fa-truck', permission: ['supplier.view', 'supplier.view_own'] },
            { id: 'customers', label: 'Customers', icon: 'fa fa-users', permission: ['customer.view', 'customer.view_own'] },
            { id: 'customer_groups', label: 'Customer Groups', icon: 'fa fa-layer-group', permission: ['customer.view', 'customer.view_own'] },
            { id: 'import_contacts', label: 'Import Contacts', icon: 'fa fa-upload', permission: ['supplier.create', 'customer.create'] },
          ]
        },
        { id: 'manufacturing', label: 'Manufacturing', icon: '🏭', permission: 'manufacturing.access_manufacturing_module' },
        { id: 'repair', label: 'Repair', icon: '🔧', permission: ['repair.view', 'job_sheet.view_assigned', 'job_sheet.view_all'] },
        { id: 'project', label: 'Project', icon: '📁', permission: ['project.view_all_projects', 'project.view_own_projects'] },
        {
          id: 'sell',
          label: 'Sell',
          icon: '💰',
          permission: ['sell.view', 'sell.create', 'direct_sell.access', 'view_own_sell_only', 'view_commission_agent_sell', 'access_shipping', 'access_own_shipping', 'access_commission_agent_shipping', 'access_sell_return', 'direct_sell.view', 'direct_sell.update', 'access_own_sell_return'],
          subItems: [
            { id: 'all_sales', label: 'All Sales', icon: 'fa fa-list', permission: ['sell.view', 'sell.create', 'direct_sell.access', 'direct_sell.view', 'view_own_sell_only', 'view_commission_agent_sell', 'access_shipping', 'access_own_shipping', 'access_commission_agent_shipping'] },
            { id: 'add_sale', label: 'Add Sale', icon: 'fa fa-plus', permission: 'direct_sell.access', path: '/sell/add' },
            { id: 'list_pos', label: 'List POS', icon: 'fa fa-list-alt', permission: ['sell.view', 'sell.create', 'view_own_sell_only'], path: '/sell/pos-list' },
            { id: 'pos', label: 'POS', icon: 'fa fa-calculator', permission: ['sell.create', 'sell.view'], path: '/pos' },
            { id: 'sales_returns', label: 'Sales Returns', icon: 'fa fa-undo', permission: ['access_sell_return', 'access_own_sell_return'] },
            { id: 'shipment', label: 'Shipment', icon: 'fa fa-truck', permission: ['access_shipping', 'access_own_shipping', 'access_commission_agent_shipping'] },
            { id: 'discount', label: 'Discount', icon: 'fa fa-percent', permission: 'discount.access' },
            { id: 'add_quotation', label: 'Add Quotation', icon: 'fa fa-plus', permission: 'direct_sell.access', path: '/sell/add-quotation' },
            { id: 'list_quotation', label: 'List Quotation', icon: 'fa fa-file-text', permission: ['quotation.view_all', 'quotation.view_own'] },
            { id: 'sales_order', label: 'Sales Order', icon: 'fa fa-shopping-bag', permission: ['so.view_all', 'so.view_own'] },
            { id: 'add_draft', label: 'Add Draft', icon: 'fa fa-plus', permission: 'direct_sell.access', path: '/sell/add-draft' },
            { id: 'list_draft', label: 'List Draft', icon: 'fa fa-edit', permission: ['draft.view_all', 'draft.view_own'] },
          ]
        },
        {
          id: 'products',
          label: 'Products',
          icon: '📦',
          permission: ['product.view', 'product.create', 'brand.view', 'unit.view', 'category.view', 'brand.create', 'unit.create', 'category.create'],
          subItems: [
            { id: 'all_products', label: 'All Products', icon: 'fa fa-cube', permission: 'product.view' },
            { id: 'add_product', label: 'Add Product', icon: 'fa fa-plus', permission: 'product.create', path: '/products/add' },
            { id: 'update_price', label: 'Update Price', icon: 'fa fa-tag', permission: 'product.create' },
            { id: 'variation', label: 'Variation', icon: 'fa fa-th-large', permission: 'product.create' },
            { id: 'import_product', label: 'Import Products', icon: 'fa fa-upload', permission: 'product.create' },
            { id: 'selling_price_group', label: 'Selling Price Group', icon: 'fa fa-dollar-sign', permission: 'product.create' },
            { id: 'units', label: 'Units', icon: 'fa fa-balance-scale', permission: ['unit.view', 'unit.create'] },
            { id: 'categories', label: 'Categories', icon: 'fa fa-list', permission: ['category.view', 'category.create'] },
            { id: 'brands', label: 'Brands', icon: 'fa fa-copyright', permission: ['brand.view', 'brand.create'] },
            { id: 'warranties', label: 'Warranties', icon: 'fa fa-shield', permission: null },
          ]
        },
        {
          id: 'purchases',
          label: 'Purchases',
          icon: '🛒',
          permission: ['purchase.view', 'purchase.create', 'purchase.update'],
          subItems: [
            { id: 'purchase_requisition', label: 'Purchase Requisition', icon: 'fa fa-file-text', permission: ['purchase_requisition.view_all', 'purchase_requisition.view_own'] },
            { id: 'purchase_order', label: 'Purchase Order', icon: 'fa fa-shopping-cart', permission: ['purchase_order.view_all', 'purchase_order.view_own'] },
            { id: 'list_purchases', label: 'List Purchases', icon: 'fa fa-list', permission: ['purchase.view', 'view_own_purchase'] },
            { id: 'add-purchase', label: 'Add Purchase', icon: 'fa fa-plus', permission: 'purchase.create', path: '/add-purchase' },
            { id: 'purchase_return', label: 'List Purchase Return', icon: 'fa fa-undo', permission: 'purchase.update' },
          ]
        },
      ]
    },
    {
      title: 'Transactions',
      items: [
        {
          id: 'stock-transfers',
          label: 'Stock Transfers',
          icon: '🔄',
          permission: ['purchase.view', 'purchase.create'],
          subItems: [
            { id: 'list_transfers', label: 'List Stock Transfers', icon: 'fa fa-list', permission: 'purchase.view' },
            { id: 'add_transfer', label: 'Add Stock Transfer', icon: 'fa fa-plus', permission: 'purchase.create' },
          ]
        },
        {
          id: 'stock-adjustment',
          label: 'Stock Adjustment',
          icon: '⚖️',
          permission: ['purchase.view', 'purchase.create'],
          subItems: [
            { id: 'list_stock_adjustment', label: 'List Stock Adjustment', icon: 'fa fa-list', permission: 'purchase.view' },
            { id: 'add_stock_adjustment', label: 'Add Stock Adjustment', icon: 'fa fa-plus', permission: 'purchase.create' },
          ]
        },
      ]
    },
    {
      title: 'Financial',
      items: [
        {
          id: 'expenses',
          label: 'Expenses',
          icon: '💸',
          permission: ['all_expense.access', 'view_own_expense'],
          subItems: [
            { id: 'list_expenses', label: 'List Expenses', icon: 'fa fa-list', permission: ['all_expense.access', 'view_own_expense'] },
            { id: 'expense_categories', label: 'Expense Categories', icon: 'fa fa-tags', permission: ['expense.add', 'expense.edit'] },
          ]
        },
        {
          id: 'payment-accounts',
          label: 'Payment Accounts',
          icon: '💳',
          permission: 'account.access',
          subItems: [
            { id: 'list_accounts', label: 'List Accounts', icon: 'fa fa-list', permission: 'payment_account.view' },
            { id: 'account_types', label: 'Account Types', icon: 'fa fa-tags', permission: 'payment_account.view' },
            { id: 'balance_sheet', label: 'Balance Sheet', icon: 'fa fa-file-invoice-dollar', permission: 'payment_account.view' },
            { id: 'trial_balance', label: 'Trial Balance', icon: 'fa fa-balance-scale', permission: 'payment_account.view' },
            { id: 'cash_flow', label: 'Cash Flow', icon: 'fa fa-exchange-alt', permission: 'payment_account.view' },
            { id: 'payment_report', label: 'Payment Report', icon: 'fa fa-chart-line', permission: 'account.access' },
          ]
        },
        { id: 'accounting', label: 'Accounting', icon: '📊', permission: 'accounting.access_accounting_module' },
        { id: 'reports', label: 'Reports', icon: '📈', permission: ['purchase_n_sell_report.view', 'contacts_report.view', 'stock_report.view', 'tax_report.view', 'trending_product_report.view', 'sales_representative.view', 'register_report.view', 'expense_report.view'] },
      ]
    },
    {
      title: 'System',
      items: [
        { id: 'notification-templates', label: 'Notification Templates', icon: '📧', permission: 'send_notifications' },
        { id: 'settings', label: 'Settings', icon: '⚙️', permission: ['business_settings.access', 'barcode_settings.access', 'invoice_settings.access', 'tax_rate.view', 'tax_rate.create', 'access_package_subscriptions'] },
        { id: 'crm', label: 'CRM', icon: '🤝', permission: ['crm.access_all_schedule', 'crm.access_own_schedule'] },
        { id: 'hrm', label: 'HRM', icon: '👥', permission: ['essentials.crud_own_leave', 'essentials.crud_all_leave', 'essentials.view_own_attendance', 'essentials.crud_all_attendance'] },
        { id: 'essentials', label: 'Essentials', icon: '✅', permission: ['essentials.add_todos', 'essentials.assign_todos'] },
        { id: 'woocommerce', label: 'Woocommerce Connector', icon: '🌐', permission: ['woocommerce.syc_categories', 'woocommerce.sync_products', 'woocommerce.sync_orders', 'woocommerce.map_tax_rates', 'woocommerce.access_woocommerce_api_settings'] },
        { id: 'connector', label: 'Connector', icon: '🔌', permission: 'connector.access_connector_module', isWebLink: true, path: '/connector' },
        { id: 'plaid', label: 'Plaid Banking', icon: '🏦', permission: 'plaid_banking.access' },
      ]
    },
    {
      title: 'AI Tools',
      items: [
        { id: 'ai-assistant', label: 'AI Assistant', icon: '🤖', permission: null },
      ]
    }
  ];

  const location = useLocation();
  const navigate = useNavigate();

  // Scroll to top whenever route changes
  useEffect(() => {
    if (contentWrapperRef.current) {
      contentWrapperRef.current.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Update active tab based on URL synchronously to prevent flash
  React.useLayoutEffect(() => {
    const cleanPath = location.pathname.replace(/\/$/, ''); // Remove trailing slash
    const isSalesOrderAdd = cleanPath === '/sell/add-sales-order' ||
      (cleanPath === '/sell/add' && (location.state?.sale_type === 'sales_order' || location.search.includes('sale_type=sales_order')));

    const routeTabMap = {
      '/sell/sales-order': 'sales_order',
      '/sell/sales-orders': 'sales_order',
      '/sales_order': 'sales_order',
      '/sales-order': 'sales_order',
      '/sell/add-sales-order': 'sales_order',
      '/sell/pos-list': 'list_pos',
      '/pos': 'pos',
      '/sell/add': isSalesOrderAdd ? 'sales_order' : 'add_sale',
      '/sell/add-draft': 'add_draft',
      '/sell/add-quotation': 'add_quotation',
      '/sell/quotation-list': 'list_quotation',
      '/sell/quotations': 'list_quotation',
      '/sell/draft-list': 'list_draft',
      '/sell/drafts': 'list_draft',
      '/sell/sales-returns': 'sales_returns',
      '/sell/shipment': 'shipment',
      '/sell/shipments': 'shipment',
      '/sell/discount': 'discount',
      '/sell/discounts': 'discount',
      '/sell/all': 'all_sales',
      '/sell': 'all_sales',
      '/all_sales': 'all_sales',
      '/purchase_requisition': 'purchase_requisition',
      '/purchases/requisition': 'purchase_requisition',
      '/purchase_order': 'purchase_order',
      '/purchases/order': 'purchase_order',
      '/purchase_return': 'purchase_return',
      '/purchases/return': 'purchase_return',
      '/list_purchases': 'list_purchases',
      '/purchases': 'list_purchases',
    };

    if (routeTabMap[cleanPath]) {
      setActiveTab(routeTabMap[cleanPath]);
    } else if (cleanPath.startsWith('/purchase-return/')) {
      setActiveTab('purchase_return');
    } else if (cleanPath.startsWith('/purchases/order/')) {
      setActiveTab('purchase_order');
    } else if (cleanPath.startsWith('/purchases/requisition/')) {
      setActiveTab('purchase_requisition');
    } else if (cleanPath.startsWith('/sell/')) {
      setActiveTab('sell');
    } else {
      const pathParts = location.pathname.split('/').filter(p => p);
      const firstPath = pathParts[0] || 'home';
      if (firstPath) {
        setActiveTab(firstPath);
      } else {
        setActiveTab('home');
      }
    }
  }, [location]);

  const handleTabChange = (tabId, customPath, isWebLink = false) => {
    setActiveTab(tabId);
    // Only close sidebar on mobile (screen width <= 768px)
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }

    if (isWebLink) {
      window.location.href = customPath || `/${tabId}`;
      return;
    }

    // Use custom path if provided, otherwise use default navigation
    if (customPath) {
      navigate(customPath);
    } else if (tabId === 'home') {
      navigate('/home');
    } else {
      navigate(`/${tabId}`);
    }
  };

  const toggleSubmenu = (menuId) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const renderContent = () => {
    // If we're on a nested route for users, render the outlet
    if (location.pathname.includes('users/') ||
      location.pathname.includes('products/') ||
      location.pathname.includes('roles/') ||
      location.pathname.includes('contacts/') ||
      location.pathname.includes('sales-commission-agents/') ||
      location.pathname.startsWith('/ai-assistant') ||
      location.pathname === '/profile' ||
      location.pathname.startsWith('/purchases/order/') ||
      location.pathname.startsWith('/purchases/requisition/') ||
      location.pathname.startsWith('/purchase_requisition/') ||
      location.pathname.startsWith('/purchases/return/') ||
      location.pathname.startsWith('/purchase-return/')) {
      return <Outlet />;
    }

    // Otherwise render the tab content as before
    switch (activeTab) {
      case 'home': return <Home currentUser={currentUser} />;
      case 'users':
      case 'roles':
      case 'agents':
        return <Users activeTab={activeTab} />;
      case 'contacts':
      case 'suppliers':
      case 'customers':
      case 'customer_groups':
      case 'import_contacts':
        return <Contacts activeTab={activeTab} />;
      case 'products':
      case 'all_products':
      case 'add_product':
      case 'units':
      case 'warranties':
      case 'brands':
      case 'update_price':
      case 'variation':
      case 'import_product':
      case 'categories':
      case 'selling_price_group':
        return <Products activeTab={activeTab} />;
      case 'purchases':
      case 'list_purchases':
      case 'add_purchase':
      case 'purchase_order':
      case 'purchase_requisition':
      case 'purchase_return':
        return <Purchases activeTab={activeTab} />;
      case 'add-purchase': return <AddPurchase onBack={() => setActiveTab('purchases')} />;
      case 'sell':
      case 'all_sales':
      case 'add_sale':
      case 'list_pos':
      case 'sales_returns':
      case 'shipment':
      case 'discount':
      case 'add_quotation':
      case 'list_quotation':
      case 'sales_order':
      case 'add_draft':
      case 'list_draft':
        return <Sell activeTab={activeTab} />;
      case 'stock-transfers':
      case 'list_transfers':
      case 'add_transfer':
        return <StockTransfers activeTab={activeTab} />;
      case 'stock-adjustment':
      case 'list_stock_adjustment':
      case 'add_stock_adjustment':
        return <StockAdjustment activeTab={activeTab} />;
      case 'expenses':
      case 'list_expenses':
      case 'expense_categories':
        return <Expenses activeTab={activeTab} />;
      case 'payment-accounts':
      case 'list_accounts':
      case 'account_types':
      case 'balance_sheet':
      case 'trial_balance':
      case 'cash_flow':
      case 'payment_report':
        return <PaymentAccounts activeTab={activeTab} />;
      case 'accounting': return <Accounting />;
      case 'reports': return <Reports />;
      case 'notification-templates': return <NotificationTemplates />;
      case 'settings': return <Settings />;
      case 'crm':
      case 'crm_dashboard':
      case 'crm_leads':
      case 'crm_followups':
      case 'crm_campaigns':
      case 'crm_contactslogin':
      case 'crm_commissions':
      case 'crm_reports':
      case 'crm_proposal_template':
      case 'crm_proposals':
      case 'crm_call_log':
      case 'crm_sources':
      case 'crm_life_stage':
      case 'crm_followup_category':
      case 'crm_settings':
        return <SubscriptionGuard module="crm"><CRM activeTab={activeTab} /></SubscriptionGuard>;
      case 'manufacturing':
      case 'recipe':
      case 'production':
      case 'mfg_settings':
      case 'mfg_report':
        return <SubscriptionGuard module="manufacturing"><Manufacturing activeTab={activeTab} /></SubscriptionGuard>;
      case 'repair':
      case 'job_sheets':
      case 'add_job_sheet':
      case 'list_invoices':
      case 'add_invoice':
      case 'brands':
      case 'repair_settings':
        return <SubscriptionGuard module="repair"><Repair activeTab={activeTab} /></SubscriptionGuard>;
      case 'project':
      case 'projects':
      case 'my_tasks':
      case 'project_categories':
      case 'project_settings':
        return <SubscriptionGuard module="project"><Project activeTab={activeTab} /></SubscriptionGuard>;
      case 'hrm':
      case 'hrm_dashboard':
      case 'hrm_leave_type':
      case 'hrm_leave':
      case 'hrm_attendance':
      case 'hrm_payroll':
      case 'hrm_holiday':
      case 'hrm_departments':
      case 'hrm_designations':
      case 'hrm_sales_target':
      case 'hrm_settings':
        return <SubscriptionGuard module="essentials"><HRM activeTab={activeTab} /></SubscriptionGuard>;
      case 'essentials':
      case 'essentials_todo':
      case 'essentials_document':
      case 'essentials_memos':
      case 'essentials_reminders':
      case 'essentials_messages':
      case 'essentials_knowledge_base':
      case 'essentials_settings':
        return <SubscriptionGuard module="essentials"><Essentials activeTab={activeTab} /></SubscriptionGuard>;
      case 'woocommerce': return <SubscriptionGuard module="woocommerce"><WoocommerceConnector /></SubscriptionGuard>;
      case 'plaid': return <SubscriptionGuard module="plaid"><PlaidConnector /></SubscriptionGuard>;
      // SuperAdmin routes
      case 'system-settings':
      case 'superadmin-dashboard':
      case 'superadmin-business':
      case 'superadmin-subscription':
      case 'superadmin-packages':
      case 'superadmin-coupons':
      case 'superadmin-settings':
      case 'superadmin-communicator':
        return <SystemSettings activeTab={activeTab} />;
      case 'ai-assistant': return <Outlet />;
      default: return <Home />;
    }
  };

  // Show loading screen only on initial mount (when currentUser has never been set).
  // Once currentUser is populated it is NEVER reset to null, so this screen
  // will not flash again during background reloads or settings updates.
  if (!currentUser) {
    const savedUserStr = localStorage.getItem('etpos_user');
    let savedUser = null;
    try {
      if (savedUserStr) savedUser = JSON.parse(savedUserStr);
    } catch (e) {}

    const businessName = currentUser?.business?.name || currentUser?.business_name || localStorage.getItem('etpos_business_name') || savedUser?.business?.name || savedUser?.business_name || 'Your Business';
    const rawLogo = currentUser?.business?.logo || currentUser?.business_logo || localStorage.getItem('etpos_business_logo') || savedUser?.business?.logo || savedUser?.business_logo;

    let businessLogo = null;
    if (rawLogo) {
      businessLogo = rawLogo.startsWith('http') 
        ? rawLogo 
        : `https://et-pos.store/uploads/business_logos/${rawLogo}`;
    }

    return (
      <div className="etpos-dashboard" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)'
      }}>
        <div style={{
          textAlign: 'center',
          color: 'white',
          maxWidth: '480px',
          padding: '0 20px'
        }}>
          <div style={{
            margin: '0 auto 1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'pulse 2s ease-in-out infinite'
          }}>
            {businessLogo ? (
              <img 
                src={businessLogo} 
                alt={businessName} 
                style={{ width: 'auto', height: 'auto', maxWidth: '300px', maxHeight: '150px', objectFit: 'contain' }} 
                onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'block'; }} 
              />
            ) : null}
            <span style={{ fontSize: '2.5rem', display: businessLogo ? 'none' : 'block' }}>🏪</span>
          </div>
          <h2 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '2.2rem',
            marginBottom: '1rem',
            fontWeight: '800',
            letterSpacing: '-0.02em'
          }}>
            {businessName}
          </h2>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.6rem',
            fontSize: '1.05rem',
            marginTop: '1.2rem',
            color: '#c7d2fe'
          }}>
            <svg
              className="circular-arrow-spinner"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: '22px', height: '22px' }}
            >
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
            </svg>
            <span>Loading your workspace...</span>
          </div>
          <p style={{
            marginTop: '0.5rem',
            fontSize: '0.88rem',
            color: '#94a3b8'
          }}>
            Please wait while we load your permissions and business data
          </p>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.04); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="etpos-dashboard">
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: '#667eea',
            zIndex: 10000,
          }
        }}
      />
      {isImpersonating && (
        <div style={{
          background: '#fcf8e3',
          borderBottom: '1px solid #fbeed5',
          color: '#c09853',
          padding: '10px 15px',
          textAlign: 'center',
          fontSize: '0.9rem',
          fontWeight: '600',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          zIndex: 9999
        }}>
          <span>⚡ You are logged in as user: <strong>{currentUser?.username || 'User'}</strong> ({currentUser?.business?.name || currentUser?.business_name || 'Business'})</span>
          <button 
            onClick={handleSwitchBackToSuperadmin}
            style={{
              background: '#f0ad4e',
              border: 'none',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              marginLeft: '10px',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = '#ec971f'}
            onMouseOut={(e) => e.target.style.background = '#f0ad4e'}
          >
            ↺ Switch Back to Superadmin
          </button>
        </div>
      )}
      {/* Header */}
      <header className="etpos-header">
        <div className="header-left">
          <button
            className="menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <h1 className="store-title">
            {(() => {
              const storeName = currentUser?.business?.name || currentUser?.business_name || 'ET-POS Store';
              // Extract first and last letters for abbreviation
              const words = storeName.trim().split(/\s+/);
              let abbreviation = '';
              if (words.length > 1) {
                // Multi-word: first letter of first word + first letter of last word
                const firstLetter = words[0].charAt(0).toUpperCase();
                const lastLetter = words[words.length - 1].charAt(0).toUpperCase();
                abbreviation = `${firstLetter}${lastLetter}`;
              } else {
                // Single word: first and last letter of the word
                const firstLetter = storeName.charAt(0).toUpperCase();
                const lastLetter = storeName.charAt(storeName.length - 1).toUpperCase();
                abbreviation = `${firstLetter}${lastLetter}`;
              }
              return (
                <>
                  <span className="store-title-full">{storeName}</span>
                  <span className="store-title-abbr">{abbreviation}</span>
                </>
              );
            })()}
          </h1>
          <div className="header-btn date-btn desktop-only-item" style={{ cursor: 'default', marginLeft: '1.5rem' }}>
            <LiveClock />
          </div>
        </div>

        <div className="header-center">
          {/* Date filter moved to Home dashboard */}
        </div>

        <div className="header-right">
          {(!enabledModules || enabledModules.length === 0 || enabledModules.includes('pos_sale') || enabledModules.includes('pos')) && hasPermission('sell.create') && (
            <button
              className="header-btn pos-btn"
              onClick={() => navigate('/pos')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                fontWeight: '600'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
              </svg>
              POS
            </button>
          )}

          <div className="header-btn-dropdown-container" ref={todoDropdownRef} style={{ position: 'relative' }}>
            <button
              className="header-btn todo-btn"
              onClick={() => setTodoDropdownOpen(!todoDropdownOpen)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600' }}
            >
              🛠️ <span className="todo-text" style={{ fontWeight: '600' }}>Utilities</span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{
                  transform: todoDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}
              >
                <path d="M3 4.5L6 7.5L9 4.5" />
              </svg>
            </button>

            {todoDropdownOpen && (
              <div
                className="user-dropdown-menu"
                style={{
                  display: 'block',
                  top: '120%',
                  right: 'auto',
                  left: '0',
                  width: '200px',
                  marginTop: '0'
                }}
              >
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setTodoDropdownOpen(false);
                    setShowCalendar(!showCalendar);
                  }}
                >
                  📅 Calendar
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setTodoDropdownOpen(false);
                    setShowCalculatorPopup(!showCalculatorPopup);
                  }}
                >
                  🧮 Calculator
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => { 
                    setTodoDropdownOpen(false);
                    // Slight delay to allow dropdown to close before starting tour
                    setTimeout(() => setRunTour(true), 100);
                  }}
                >
                  🎯 Application Tour
                </button>

                <div className="dropdown-divider"></div>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setTodoDropdownOpen(false);
                    setSupportOpen(true);
                  }}
                >
                  🎧 Support
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => { setDarkMode(!darkMode); setTodoDropdownOpen(false); }}
                >
                  {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setTodoDropdownOpen(false);
                    setShowTodayProfitModal(true);
                    fetchTodayProfitData();
                  }}
                >
                  💰 Today's Profit
                </button>
              </div>
            )}

            {/* Mini Calendar Popup */}
            {showCalendar && (
              <div
                ref={calendarRef}
                style={{
                  position: 'absolute',
                  top: '120%',
                  right: '0',
                  zIndex: 1002,
                  background: 'white',
                  padding: '10px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  border: '1px solid #e2e8f0'
                }}
              >
                <DatePicker
                  selected={calendarDate}
                  onChange={(date) => setCalendarDate(date)}
                  inline
                />
              </div>
            )}

            {/* Calculator Popup */}
            {showCalculatorPopup && (
              <div ref={calculatorRef}>
                <Calculator onClose={() => setShowCalculatorPopup(false)} />
              </div>
            )}

            {/* Today's Profit Modal */}
            {showTodayProfitModal && (
              <div ref={todayProfitRef}>
                <TodayProfitModal
                  isOpen={showTodayProfitModal}
                  onClose={() => setShowTodayProfitModal(false)}
                  profitData={todayProfitData}
                />
              </div>
            )}
          </div>

          <div className="notification-section" ref={notificationRef}>
            <button className="notification-btn" onClick={toggleNotificationDropdown}>
              🔔
              {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </button>
            {notificationDropdownOpen && (
              <div className="notification-dropdown">
                <div className="notification-dropdown-header">Notifications</div>
                <div className="notification-dropdown-list">
                  {notificationsData.length > 0 ? (
                    notificationsData.slice(0, 10).map((notif, index) => (
                      <div 
                        className={`notification-dropdown-item ${!notif.read_at ? 'unread' : ''}`} 
                        key={index}
                        onClick={() => {
                          if (notif.link && notif.link !== '#') {
                            window.open(notif.link, '_blank');
                          }
                        }}
                        style={{ cursor: notif.link && notif.link !== '#' ? 'pointer' : 'default', ...(notif.read_at ? { opacity: 0.7 } : {}) }}
                      >
                        <div className="notification-dropdown-title" dangerouslySetInnerHTML={{ __html: notif.msg }}></div>
                        <div className="notification-dropdown-meta" style={{ marginTop: '4px', fontSize: '0.75rem', color: '#64748b' }}>{notif.created_at}</div>
                      </div>
                    ))
                  ) : (
                    <div className="notification-dropdown-item" style={{ textAlign: 'center', color: '#94a3b8' }}>
                      No notifications found
                    </div>
                  )}
                </div>
                <div className="notification-dropdown-footer" onClick={() => { setNotificationDropdownOpen(false); setActiveTab('notification-templates'); }}>
                  View All
                </div>
              </div>
            )}
          </div>
          <div className="user-section" onClick={toggleUserDropdown} ref={userSectionRef}>
            <span className="user-name">
              {currentUser ?
                `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username || 'User'
                : 'Loading...'}
            </span>
            <div className="profile-logo">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>

            {/* Dropdown Menu */}
            {userDropdownOpen && (
              <div className="user-dropdown-menu">
                <div className="dropdown-header-item">
                  <div className="signed-in-text">Signed in as</div>
                  <div className="signed-in-name">
                    {currentUser ?
                      `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username || 'User'
                      : 'User'}
                  </div>
                </div>
                <div className="dropdown-divider"></div>

                <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); navigate('/profile'); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  Profile
                </button>

                <button className="dropdown-item logout-item" onClick={(e) => { e.stopPropagation(); handleLogout(); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="etpos-body">
        {/* Sidebar */}
        <aside className={`etpos-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-content" onScroll={() => setHoveredItem(null)}>
            {menuCategories.map((category, index) => {
              // Check if user has backend is_superadmin flag or superadmin context state (excluded when impersonating tenant user)
              const userRoles = currentUser?.roles || [];
              const isSuperAdmin = !isImpersonating && (isSuperAdminFromContext || currentUser?.is_superadmin === true || currentUser?.is_superadmin === 1 || currentUser?.user_type === 'superadmin');

              // Check if user is an Admin (matches web $is_admin logic)
              const isAdmin = userRoles.some(role => {
                const name = role.name || '';
                return name.toLowerCase().startsWith('admin') || name.toLowerCase() === 'administrator';
              });

              // Helper to check permission (string or array)
              // Admin users bypass permission checks for Sell menu (matches web $is_admin)
              const checkPerm = (perm, isSellItem = false) => {
                if (!perm) return true;
                if (isAdmin && isSellItem) return true;
                if (Array.isArray(perm)) return hasAnyPermission(perm);
                return hasPermission(perm);
              };

              // Filter items based on permissions, and also filter their subItems
              let filteredItems = category.items.map(item => {
                if (item.subItems) {
                  const isSellParent = item.id === 'sell';
                  const filteredSubItems = item.subItems.filter(sub => {
                    if (sub.id === 'purchase_order') {
                      const enabledPO = localStorage.getItem('etpos_enable_purchase_order');
                      if (enabledPO !== '1' && enabledPO !== 'true') return false;
                    }
                    if (sub.id === 'purchase_requisition') {
                      const enabledPR = localStorage.getItem('etpos_enable_purchase_requisition');
                      if (enabledPR !== '1' && enabledPR !== 'true') return false;
                    }
                    return checkPerm(sub.permission, isSellParent);
                  });
                  return { ...item, subItems: filteredSubItems };

                }
                return item;
              }).filter(item => {
                if (item.subItems) {
                  return item.subItems.length > 0;
                }
                return checkPerm(item.permission);
              });

              // Filter Superadmin tab based on role
              if (!isSuperAdmin) {
                filteredItems = filteredItems.filter(item => item.id !== 'system-settings');
              }

              // ─── Subscription module gating ───
              if (!isSuperAdmin && !subscriptionLoading) {
                const moduleGates = {
                  'crm': 'crm',
                  'woocommerce': 'woocommerce',
                  'plaid': 'plaid',
                  'ai-assistant': 'ai_assistance',
                  'manufacturing': 'manufacturing',
                  'repair': 'repair',
                  'project': 'project',
                  'hrm': 'essentials',
                  'essentials': 'essentials',
                  'connector': 'connector',
                };
                filteredItems = filteredItems.filter(item => {
                  const requiredModule = moduleGates[item.id];
                  if (!requiredModule) return true;
                  return hasModule(requiredModule);
                });
              }

              // ─── Business enabled_modules gating ───
              const businessModuleGates = {
                'payment-accounts': 'account',
                'purchases': 'purchases',
                'stock-transfers': 'stock_transfers',
                'stock-adjustment': 'stock_adjustment',
                'expenses': 'expenses',
              };
              filteredItems = filteredItems.filter(item => {
                const requiredBusinessModule = businessModuleGates[item.id];
                if (!requiredBusinessModule) return true;
                return enabledModules.includes(requiredBusinessModule);
              });

              // Only show category if it has visible items
              if (filteredItems.length === 0) return null;

              return (
                <div key={index} className="menu-category">
                  <div className="category-title">
                    {category.title}
                    {category.title === 'Main' && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', fontWeight: '600', color: '#f59e0b' }}>V6.5</span>}
                  </div>
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className="nav-item-wrapper"
                      data-label={item.label}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setHoveredItem({
                          id: item.id,
                          top: sidebarOpen ? (rect.top + rect.height / 2) : rect.top
                        });
                      }}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      <button
                        className={`nav-item ${activeTab === item.id || (item.subItems && item.subItems.some(sub => sub.id === activeTab)) ? 'active' : ''}`}
                        onClick={(e) => {
                          if (item.subItems && window.innerWidth <= 768) {
                            e.preventDefault();
                            e.stopPropagation();
                            setClickedMenuId(clickedMenuId === item.id ? null : item.id);
                          } else {
                            if (item.subItems) {
                              handleTabChange(item.subItems[0].id);
                            } else {
                              handleTabChange(item.id, item.path, item.isWebLink);
                            }
                          }
                        }}
                      >
                        <span className="nav-icon">{item.icon}</span>
                        <span className="nav-label">{item.label}</span>
                      </button>

                      {/* Mobile Inline Submenu Accordion */}
                      {item.subItems && clickedMenuId === item.id && window.innerWidth <= 768 && (
                        <div className="mobile-inline-submenu">
                          {item.subItems.map((subItem) => (
                            <button
                              key={subItem.id}
                              className={`nav-item mobile-submenu-item ${activeTab === subItem.id ? 'active' : ''}`}
                              onClick={() => handleTabChange(subItem.id, subItem.path)}
                              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                            >
                              {subItem.icon && (
                                <span className="submenu-icon-wrapper" style={{ display: 'inline-flex', alignItems: 'center', width: '20px', justifyContent: 'center' }}>
                                  {subItem.icon.startsWith('fa') ? (
                                    <i className={subItem.icon} style={{ fontSize: '0.9rem', opacity: 0.75 }}></i>
                                  ) : (
                                    subItem.icon
                                  )}
                                </span>
                              )}
                              <span className="nav-label">{subItem.label}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Desktop Hover Submenu */}
                      {item.subItems && (
                        <div
                          className="submenu-container"
                          style={
                            hoveredItem && hoveredItem.id === item.id
                              ? { top: `${hoveredItem.top}px` }
                              : {}
                          }
                        >
                          <div className="hover-submenu-header">{item.label}</div>
                          <div className="submenu-items">
                            {item.subItems.map((subItem) => (
                              <button
                                key={subItem.id}
                                className={`nav-item submenu-item ${activeTab === subItem.id ? 'active' : ''}`}
                                onClick={() => {
                                  setHoveredItem(null);
                                  handleTabChange(subItem.id, subItem.path);
                                }}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                              >
                                {subItem.icon && (
                                  <span className="submenu-icon-wrapper" style={{ display: 'inline-flex', alignItems: 'center', width: '20px', justifyContent: 'center' }}>
                                    {subItem.icon.startsWith('fa') ? (
                                      <i className={subItem.icon} style={{ fontSize: '0.9rem', opacity: 0.75 }}></i>
                                    ) : (
                                      subItem.icon
                                    )}
                                  </span>
                                )}
                                <span className="nav-label">{subItem.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          <div className="sidebar-footer">
            <button className="sidebar-toggle-btn" title={sidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"} onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? '◀ Collapse Sidebar' : '▶'}
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="etpos-main-content">
          <div className="content-wrapper" ref={contentWrapperRef}>
            {renderContent()}
          </div>
        </main>
      </div>

      {/* WhatsApp Support floating widget */}
      <WhatsAppSupport
        open={supportOpen}
        setOpen={setSupportOpen}
        defaultVersion={WHATSAPP_API_VERSION}
        defaultPhoneNumberId={WHATSAPP_PHONE_NUMBER_ID}
        defaultAccessToken={WHATSAPP_ACCESS_TOKEN}
        defaultSupportNumber={SUPPORT_WHATSAPP_NUMBER}
      />

      {/* Footer */}
      <footer className="etpos-footer">
        <span>et-pos - V6.5 | Copyright © 2025 All rights reserved.</span>
      </footer>
    </div>
  );
};

export default ETPOSDashboard;
